import React, { useState, useEffect } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Help as HelpIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import locationService from "../services/locationService";

const LocationTroubleshooting = ({ onLocationFixed }) => {
  const [permissionStatus, setPermissionStatus] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [instructions, setInstructions] = useState(null);

  useEffect(() => {
    checkPermissionStatus();
    setInstructions(locationService.getLocationPermissionInstructions());
  }, []);

  const checkPermissionStatus = async () => {
    setIsChecking(true);
    try {
      const status = await locationService.checkLocationPermissionStatus();
      setPermissionStatus(status);
    } catch (error) {
      setPermissionStatus({
        status: "error",
        message: "Failed to check permission status",
        code: error.code,
      });
    } finally {
      setIsChecking(false);
    }
  };

  const handleResetPermission = () => {
    locationService.resetLocationPermission();
    checkPermissionStatus();
  };

  const handleRetryLocation = async () => {
    try {
      const location = await locationService.requestLocationPermission();
      if (onLocationFixed) {
        onLocationFixed(location);
      }
    } catch (error) {
      console.error("Retry failed:", error);
      checkPermissionStatus();
    }
  };

  const getStatusIcon = () => {
    if (!permissionStatus) return <HelpIcon />;

    switch (permissionStatus.status) {
      case "granted":
        return <CheckCircleIcon color="success" />;
      case "denied":
        return <ErrorIcon color="error" />;
      case "unavailable":
        return <WarningIcon color="warning" />;
      case "timeout":
        return <WarningIcon color="warning" />;
      default:
        return <ErrorIcon color="error" />;
    }
  };

  const getStatusColor = () => {
    if (!permissionStatus) return "default";

    switch (permissionStatus.status) {
      case "granted":
        return "success";
      case "denied":
        return "error";
      case "unavailable":
        return "warning";
      case "timeout":
        return "warning";
      default:
        return "error";
    }
  };

  const getStatusMessage = () => {
    if (!permissionStatus) return "Checking location permission...";
    return permissionStatus.message;
  };

  if (permissionStatus?.status === "granted") {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <CheckCircleIcon color="success" />
            <Typography variant="h6">Location Permission Granted</Typography>
          </Box>
          <Alert severity="success">
            Location access is working correctly! Arya can now provide
            personalized weather information.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <LocationIcon color="primary" />
            <Typography variant="h6">
              Location Permission Troubleshooting
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title="Check Permission Status">
              <IconButton onClick={checkPermissionStatus} disabled={isChecking}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Current Status */}
        <Box mb={3}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            {getStatusIcon()}
            <Typography variant="subtitle1">Current Status</Typography>
          </Box>
          <Chip
            label={getStatusMessage()}
            color={getStatusColor()}
            variant="outlined"
          />
        </Box>

        {/* Error Details */}
        {permissionStatus?.status === "denied" && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Location Permission Denied</AlertTitle>
            Your browser has blocked location access. Follow the steps below to
            enable it.
          </Alert>
        )}

        {permissionStatus?.status === "unavailable" && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Location Unavailable</AlertTitle>
            Location services may be disabled on your device. Check your device
            settings.
          </Alert>
        )}

        {permissionStatus?.status === "timeout" && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <AlertTitle>Location Request Timed Out</AlertTitle>
            The location request took too long. This might be due to poor GPS
            signal or network issues.
          </Alert>
        )}

        {/* Browser Instructions */}
        {instructions && (
          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              <SettingsIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Enable Location in {instructions.browser}
            </Typography>
            <List dense>
              {instructions.instructions.map((instruction, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Typography variant="body2" color="primary">
                      {index + 1}.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText primary={instruction} />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Action Buttons */}
        <Box display="flex" gap={2} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<LocationIcon />}
            onClick={handleRetryLocation}
            disabled={isChecking}
          >
            Try Again
          </Button>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleResetPermission}
            disabled={isChecking}
          >
            Reset & Retry
          </Button>

          <Button
            variant="text"
            onClick={checkPermissionStatus}
            disabled={isChecking}
          >
            {isChecking ? "Checking..." : "Check Status"}
          </Button>
        </Box>

        {/* Additional Help */}
        <Divider sx={{ my: 2 }} />
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Still having trouble?</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Make sure your device's location services are enabled
            <br />
            • Try refreshing the page after changing browser settings
            <br />
            • Check if other websites can access your location
            <br />• Try using a different browser or device
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LocationTroubleshooting;
